import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Papa from 'papaparse';
import { toast } from 'sonner';
import {
  ArrowLeft, Download, Upload, CheckCircle2, AlertTriangle, Loader2, FileSpreadsheet,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { assetsImportApi, allocateSqlApi } from '../services/api';
import AllocateSqlConnectForm from '../components/allocate/AllocateSqlConnectForm';

/**
 * AssetImportPage — `/assets/import`
 *
 * Four-step flow inside a single page:
 *   1. Download CSV template (header row + 1 example row from the backend)
 *   2. Upload + parse (drag-drop or file picker, papaparse)
 *   3. Map columns (auto-detect, user can remap any unmatched column)
 *   4. Preview + import (per-row preview, runs through `/api/assets/import`)
 */
const TARGET_FIELDS = [
  { key: 'name', label: 'Name', required: true },
  { key: 'serial_number', label: 'Serial number', required: true },
  { key: 'category', label: 'Category' },
  { key: 'asset_class', label: 'Asset class (main/auxiliary)' },
  { key: 'make', label: 'Make' },
  { key: 'model', label: 'Model' },
  { key: 'year', label: 'Year' },
  { key: 'hours', label: 'Hours' },
  { key: 'unit_number', label: 'Unit number' },
  { key: 'swl', label: 'SWL' },
  { key: 'swl_unit', label: 'SWL unit' },
  { key: 'depot_name', label: 'Depot name' },
  { key: 'notes', label: 'Notes' },
  // External-system id. Only used when "Source = EQUIPR Allocate" — matches and updates existing rows.
  { key: 'allocate_id', label: 'Allocate ID' },
];

const AssetImportPage = () => {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [template, setTemplate] = useState(null);

  // Restore in-progress import from localStorage so a refresh doesn't lose work.
  const STORAGE_KEY = 'guardian:asset_import_draft';
  const restored = (() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const draft = JSON.parse(raw);
      if (draft && Array.isArray(draft.rows) && draft.rows.length) return draft;
    } catch { /* ignored */ }
    return null;
  })();

  const [step, setStep] = useState(restored?.step || 'upload'); // 'upload' | 'map' | 'preview' | 'done'
  const [fileName, setFileName] = useState(restored?.fileName || null);
  const [headers, setHeaders] = useState(restored?.headers || []);
  const [rows, setRows] = useState(restored?.rows || []);
  const [mapping, setMapping] = useState(restored?.mapping || {});
  // 'csv' (default, no overwrite) | 'allocate' (CSV with id-match + update) | 'allocate_sql' (live SQL fetch).
  const [source, setSource] = useState(restored?.source || 'csv');
  // CSV only — when true, existing assets (matched by serial_number) are
  // UPDATED in place instead of being skipped as duplicates. Lets admins
  // re-import the same file to backfill columns they missed first time.
  // Empty cells don't clobber existing data (merge semantics).
  const [overwrite, setOverwrite] = useState(restored?.overwrite || false);
  // When source='allocate_sql', the SQL form hands back the body it used for preview
  // so the Confirm step can call commit with the same connection details.
  const [sqlConnectionBody, setSqlConnectionBody] = useState(null);
  const [importing, setImporting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);

  // Persist on every meaningful change. We never store the actual import result —
  // once the user is done we clear the draft entirely.
  useEffect(() => {
    if (step === 'done' || !rows.length) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ step, fileName, headers, rows, mapping, source }));
  }, [step, fileName, headers, rows, mapping, source]);

  useEffect(() => {
    assetsImportApi.getTemplate().then(({ data }) => setTemplate(data)).catch(() => {});
  }, []);

  // ── Step 1: download template ──────────────────────────────────────
  const handleDownloadTemplate = () => {
    if (!template) return;
    const header = template.columns.join(',');
    const example = template.columns.map((c) => template.example_row[c] ?? '').join(',');
    const csv = `${header}\n${example}\n`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'equipr-assets-template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  // ── Step 2: parse uploaded file ────────────────────────────────────
  const handleFile = (file) => {
    if (!file) return;
    setFileName(file.name);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h) => h.trim(),
      complete: (res) => {
        const parsedRows = res.data || [];
        if (!parsedRows.length) {
          toast.error('No rows found in this file');
          return;
        }
        const hs = res.meta.fields || [];
        setHeaders(hs);
        setRows(parsedRows);

        // Auto-map: snake_case match
        const auto = {};
        TARGET_FIELDS.forEach((t) => {
          const match = hs.find(
            (h) => h.toLowerCase().replace(/[^a-z0-9]/g, '_') === t.key ||
                   h.toLowerCase().replace(/\s+/g, '_') === t.key,
          );
          if (match) auto[t.key] = match;
        });
        setMapping(auto);
        setStep('map');
      },
      error: (err) => toast.error('Could not parse CSV: ' + err.message),
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  // ── Step 3 → 4: build the validated payload ─────────────────────────
  const remappedRows = () => rows.map((r) => {
    const out = {};
    TARGET_FIELDS.forEach((t) => {
      const src = mapping[t.key];
      if (src && r[src] !== undefined) {
        let v = String(r[src] ?? '').trim();
        if (!v) return;
        if (['year'].includes(t.key)) v = parseInt(v, 10) || null;
        else if (['hours', 'swl'].includes(t.key)) v = parseFloat(v) || null;
        if (v !== null) out[t.key] = v;
      }
    });
    return out;
  }).filter((r) => r.name || r.serial_number);  // drop completely empty rows

  const requiredMissing = TARGET_FIELDS.filter((t) => t.required && !mapping[t.key]);

  const handlePreview = async () => {
    if (requiredMissing.length) {
      toast.error(`Please map: ${requiredMissing.map((f) => f.label).join(', ')}`);
      return;
    }
    if (rows.length > (template?.max_rows || 500)) {
      toast.error(`File exceeds ${template.max_rows} rows. Split it into smaller batches.`);
      return;
    }
    if (source === 'allocate' && !mapping.allocate_id) {
      toast.error('Allocate imports need the Allocate ID column mapped.');
      return;
    }
    setPreviewing(true);
    try {
      const payload = remappedRows();
      const apiSource = source === 'allocate' ? 'allocate' : null;
      const { data } = await assetsImportApi.preview(payload, apiSource, overwrite);
      setPreview(data);
      setStep('preview');
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Preview failed.');
    } finally {
      setPreviewing(false);
    }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      let data;
      if (source === 'allocate_sql') {
        // Live SQL: rows live on the server, so commit using either the body
        // we just previewed with or saved credentials.
        if (sqlConnectionBody && sqlConnectionBody.password) {
          ({ data } = await allocateSqlApi.commit(sqlConnectionBody));
        } else {
          const changedSince = sqlConnectionBody?.changed_since || null;
          const forceResync = sqlConnectionBody?.force_resync || false;
          ({ data } = await allocateSqlApi.commitSaved(changedSince, forceResync));
        }
      } else {
        const payload = remappedRows();
        const apiSource = source === 'allocate' ? 'allocate' : null;
        ({ data } = await assetsImportApi.importRows(payload, apiSource, overwrite));
      }
      setResult(data);
      setStep('done');
      const updatedPart = data.updated ? ` · Updated ${data.updated}` : '';
      toast.success(`Imported ${data.imported}${updatedPart} · Skipped ${data.skipped} · Errors ${data.errors}`);
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'object' ? (msg.message || 'Import failed.') : (msg || 'Import failed.'));
    } finally {
      setImporting(false);
    }
  };

  // ── Render helpers ──────────────────────────────────────────────────
  return (
    <div className="space-y-8" data-testid="asset-import-page">

      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/assets')} data-testid="import-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to assets
        </Button>
      </div>

      <div>
        <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
          <FileSpreadsheet className="w-7 h-7 text-[#FFD108]" />
          Import assets from a spreadsheet
        </h1>
        <p className="text-muted-foreground mt-2 max-w-3xl">
          Move your entire fleet over in minutes. Download the template, paste in your data,
          drop the file back in, and we'll handle the rest. By default, duplicates (matched on
          serial number) are skipped — tick <strong>Overwrite existing assets</strong> below to
          update them instead (great for backfilling a column you missed).
        </p>
      </div>

      <Stepper step={step} source={source} />

      {step === 'upload' && (
        <Card data-testid="step-upload">
          <CardHeader>
            <CardTitle>1. Start with the template</CardTitle>
            <CardDescription>
              We've laid out the exact columns we need. Fill in your fleet — leave optional columns blank if you don't have the data.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                Source
              </Label>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => setSource('csv')}
                  className={`text-left rounded-lg border p-3 transition-colors ${
                    source === 'csv'
                      ? 'border-[#FFD108]/50 bg-[#FFD108]/[0.04]'
                      : 'border-border hover:border-[#FFD108]/30'
                  }`}
                  data-testid="source-csv-btn"
                >
                  <div className="font-medium text-sm">Generic CSV</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    First-time imports. Duplicate serials are <strong>skipped</strong>.
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setSource('allocate')}
                  className={`text-left rounded-lg border p-3 transition-colors ${
                    source === 'allocate'
                      ? 'border-[#FFD108]/50 bg-[#FFD108]/[0.04]'
                      : 'border-border hover:border-[#FFD108]/30'
                  }`}
                  data-testid="source-allocate-btn"
                >
                  <div className="font-medium text-sm flex items-center gap-2">
                    Allocate CSV
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-[#FFD108] text-black px-1.5 py-0.5 rounded">PRO</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Upload an SP export. Match by Allocate&nbsp;ID and <strong>update existing</strong>.
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setSource('allocate_sql')}
                  className={`text-left rounded-lg border p-3 transition-colors ${
                    source === 'allocate_sql'
                      ? 'border-[#FFD108]/50 bg-[#FFD108]/[0.04]'
                      : 'border-border hover:border-[#FFD108]/30'
                  }`}
                  data-testid="source-allocate-sql-btn"
                >
                  <div className="font-medium text-sm flex items-center gap-2">
                    Allocate Live SQL
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-[#FFD108] text-black px-1.5 py-0.5 rounded">PRO</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Connect directly. We run the SP and stream results — <strong>no file upload</strong>.
                  </div>
                </button>
              </div>
            </div>

            {/* Unified re-import toggle — visible for all 3 sources so admins
                have ONE place to opt into "update matched assets" behaviour.
                Copy adapts to the selected source. Wired through to:
                  * CSV / Allocate CSV → `overwrite` flag on /api/assets/import
                  * Allocate Live SQL → `force_resync` flag on the SQL endpoints
                                        (forwarded via the SqlConnectForm body)  */}
            <label
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                overwrite ? 'border-[#FFD108]/50 bg-[#FFD108]/[0.04]' : 'border-border hover:border-[#FFD108]/30'
              }`}
              data-testid="reimport-overwrite-label"
            >
              <input
                type="checkbox"
                checked={overwrite}
                onChange={(e) => setOverwrite(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-[#FFD108]"
                data-testid="reimport-overwrite-checkbox"
              />
              <div>
                <div className="font-medium text-sm">
                  {source === 'csv' && 'Overwrite existing assets'}
                  {source === 'allocate' && 'Force re-import (Allocate CSV)'}
                  {source === 'allocate_sql' && 'Force re-import (Allocate Live SQL)'}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {source === 'csv' && (
                    <>Re-importing? Match by <strong>serial number</strong> and update existing
                    records instead of skipping them as duplicates. Blank cells in your file
                    won't clobber existing data — perfect for backfilling a column you missed.</>
                  )}
                  {(source === 'allocate' || source === 'allocate_sql') && (
                    <>Treat every matched asset as an update — even when no field-level changes
                    were detected. Use this to re-import Allocate's values over manual Guardian
                    edits, or to refresh the sync timestamp.</>
                  )}
                </div>
              </div>
            </label>

            {source === 'allocate_sql' ? (
              <AllocateSqlConnectForm
                forceResync={overwrite}
                onPreview={(previewData, bodyUsed) => {
                  setPreview(previewData);
                  setSqlConnectionBody(bodyUsed);
                  setStep('preview');
                }}
                onSourceCommitted={(commitData) => {
                  setResult(commitData);
                  setStep('done');
                  const updatedPart = commitData.updated ? ` · Updated ${commitData.updated}` : '';
                  toast.success(
                    `Imported ${commitData.imported}${updatedPart} · Skipped ${commitData.skipped} · Errors ${commitData.errors}`
                  );
                }}
              />
            ) : (
              <>
                <Button onClick={handleDownloadTemplate} disabled={!template} variant="outline" data-testid="download-template-btn">
                  <Download className="w-4 h-4 mr-2" /> Download CSV template
                </Button>

                <div
                  onDrop={handleDrop}
                  onDragOver={(e) => e.preventDefault()}
                  className="rounded-xl border-2 border-dashed border-border hover:border-[#FFD108]/40 bg-card/40 p-10 text-center transition-colors cursor-pointer"
                  onClick={() => fileRef.current?.click()}
                  data-testid="upload-dropzone"
                >
                  <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                  <p className="font-medium">Drop your CSV here, or click to browse</p>
                  <p className="text-xs text-muted-foreground mt-1">Up to {template?.max_rows || 500} rows per upload</p>
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".csv,text/csv"
                    className="hidden"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                    data-testid="upload-input"
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {step === 'map' && (
        <Card data-testid="step-map">
          <CardHeader>
            <CardTitle>2. Map your columns</CardTitle>
            <CardDescription>
              We've auto-matched what we recognised. Pick the right CSV column for any missing fields.
              <span className="text-muted-foreground"> File: <strong>{fileName}</strong> · {rows.length} rows</span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {TARGET_FIELDS.map((t) => (
              <div key={t.key} className="grid grid-cols-1 sm:grid-cols-[200px_1fr] items-center gap-3">
                <Label className="text-sm">
                  {t.label}
                  {t.required && <span className="text-red-400 ml-1">*</span>}
                </Label>
                <select
                  value={mapping[t.key] || ''}
                  onChange={(e) => setMapping((m) => ({ ...m, [t.key]: e.target.value || null }))}
                  className="h-9 px-3 rounded-md bg-card border border-border text-sm focus:outline-none focus:border-[#FFD108]/40"
                  data-testid={`map-${t.key}`}
                >
                  <option value="">— skip —</option>
                  {headers.map((h) => (
                    <option key={h} value={h}>{h}</option>
                  ))}
                </select>
              </div>
            ))}
            <div className="flex justify-between pt-4 border-t border-border">
              <Button variant="ghost" onClick={() => setStep('upload')}>← Re-upload</Button>
              <Button className="btn-volt" onClick={handlePreview} disabled={previewing} data-testid="map-continue-btn">
                {previewing && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {previewing ? 'Building preview…' : 'Preview import →'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 'preview' && preview && (
        <Card data-testid="step-preview">
          <CardHeader>
            <CardTitle>3. Review changes</CardTitle>
            <CardDescription>
              <span className="text-emerald-400 font-semibold">{preview.summary.would_create}</span> to create
              {' · '}
              <span className="text-[#FFD108] font-semibold">{preview.summary.would_update}</span> to update
              {' · '}
              <span className="text-muted-foreground">{preview.summary.would_skip}</span> skipped
              {preview.summary.errors > 0 && (
                <>
                  {' · '}
                  <span className="text-red-400 font-semibold">{preview.summary.errors}</span> errors
                </>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {source === 'allocate_sql' && preview.sql_columns?.length > 0 && (
              <div className="rounded-lg border border-border bg-secondary/20 p-3">
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1.5">
                  SP returned these columns
                </p>
                <p className="font-mono text-[11px] text-foreground break-all">
                  {preview.sql_columns.join(', ')}
                </p>
                <p className="text-[11px] text-muted-foreground mt-1.5 leading-relaxed">
                  Guardian fields are populated from these column names (case-insensitive,
                  aliases tolerated). If <code className="font-mono">Unit #</code> looks blank
                  after import, confirm the SP returns <code className="font-mono">unit_number</code>
                  or <code className="font-mono">FleetNumber</code>.
                </p>
              </div>
            )}

            {preview.summary.errors > 0 && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/[0.04] p-3">
                <p className="text-sm font-semibold flex items-center gap-2 text-red-400 mb-2">
                  <AlertTriangle className="w-4 h-4" /> Errors will be skipped
                </p>
                <ul className="text-xs text-muted-foreground space-y-1 max-h-32 overflow-auto">
                  {preview.errors.map((e, i) => (
                    <li key={i}>Row {e.row} ({e.serial_number || '—'}): {e.error}</li>
                  ))}
                </ul>
              </div>
            )}

            {preview.would_update.length > 0 && (
              <div data-testid="preview-updates">
                <h3 className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
                  Updating {preview.would_update.length} existing asset{preview.would_update.length === 1 ? '' : 's'}
                  {source === 'allocate' && ' — Allocate values win'}
                </h3>
                <div className="space-y-2 max-h-80 overflow-auto pr-1">
                  {preview.would_update.slice(0, 30).map((u, i) => (
                    <div key={i} className="rounded-md border border-[#FFD108]/30 bg-[#FFD108]/[0.03] p-3">
                      <div className="flex items-center justify-between text-sm font-medium">
                        <span>{u.name} <span className="text-muted-foreground font-normal">· {u.serial_number}</span></span>
                        <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
                          via {u.matched_via === 'name_and_serial' ? 'name + serial' :
                               u.matched_via === 'external_id' ? 'Allocate ID' :
                               u.matched_via || 'serial'}
                        </span>
                      </div>
                      <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-xs font-mono">
                        {Object.entries(u.diff).map(([field, change]) => (
                          <div key={field} className="flex gap-2">
                            <span className="text-muted-foreground">{field}:</span>
                            <span className="text-red-400/80 line-through truncate">{String(change.from ?? '—')}</span>
                            <span className="text-muted-foreground">→</span>
                            <span className="text-emerald-400 truncate">{String(change.to ?? '—')}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  {preview.would_update.length > 30 && (
                    <p className="text-xs text-muted-foreground pl-1">
                      …and {preview.would_update.length - 30} more.
                    </p>
                  )}
                </div>
              </div>
            )}

            {preview.would_create.length > 0 && (
              <div data-testid="preview-creates">
                <h3 className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
                  Creating {preview.would_create.length} new asset{preview.would_create.length === 1 ? '' : 's'}
                </h3>
                <ul className="text-xs space-y-1 max-h-40 overflow-auto pl-1">
                  {preview.would_create.slice(0, 30).map((c, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                      <span>{c.name} <span className="text-muted-foreground">· {c.serial_number}</span></span>
                      {c.allocate_id && (
                        <span className="text-[10px] text-muted-foreground font-mono">allocate:{c.allocate_id}</span>
                      )}
                    </li>
                  ))}
                  {preview.would_create.length > 30 && (
                    <li className="text-muted-foreground">…and {preview.would_create.length - 30} more.</li>
                  )}
                </ul>
              </div>
            )}

            {preview.would_skip.length > 0 && (
              <div>
                <h3 className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
                  Skipping {preview.would_skip.length}
                </h3>
                <ul className="text-xs text-muted-foreground space-y-0.5 max-h-32 overflow-auto pl-1">
                  {preview.would_skip.slice(0, 30).map((s, i) => (
                    <li key={i}>
                      {s.name ? `${s.name} · ` : ''}{s.serial_number || '(no serial)'}: {s.reason === 'duplicate_in_file' ? 'duplicate in file (same name + serial as an earlier row)' :
                        s.reason === 'no_changes' ? 'no changes' :
                        'already in your fleet'}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex justify-between pt-2 border-t border-border">
              <Button
                variant="ghost"
                onClick={() => {
                  if (source === 'allocate_sql') {
                    setStep('upload');
                    setPreview(null);
                  } else {
                    setStep('map');
                  }
                }}
              >
                {source === 'allocate_sql' ? '← Back to connection' : '← Back to mapping'}
              </Button>
              <Button
                className="btn-volt"
                onClick={handleImport}
                disabled={importing || (preview.summary.would_create + preview.summary.would_update === 0)}
                data-testid="run-import-btn"
              >
                {importing && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {importing
                  ? 'Importing…'
                  : `Confirm — ${preview.summary.would_create} create · ${preview.summary.would_update} update`}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 'done' && result && (
        <Card data-testid="step-done">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-6 h-6 text-green-400" />
              Import complete
            </CardTitle>
            <CardDescription>
              {result.imported} created
              {result.updated ? ` · ${result.updated} updated` : ''}
              {' · '}{result.skipped} skipped · {result.errors} errors.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {result.errors > 0 && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/[0.04] p-3">
                <p className="text-sm font-semibold flex items-center gap-2 text-red-400 mb-2">
                  <AlertTriangle className="w-4 h-4" /> Errors ({result.errors})
                </p>
                <ul className="text-xs text-muted-foreground space-y-1 max-h-40 overflow-auto">
                  {result.details.errors.map((e, i) => (
                    <li key={i}>Row {e.row} ({e.serial_number || '—'}): {e.error}</li>
                  ))}
                </ul>
              </div>
            )}
            {result.skipped > 0 && (
              <div className="rounded-lg border border-[#FFD108]/30 bg-[#FFD108]/[0.04] p-3">
                <p className="text-sm font-semibold mb-2">Skipped ({result.skipped})</p>
                <ul className="text-xs text-muted-foreground space-y-1 max-h-40 overflow-auto">
                  {result.details.skipped.map((e, i) => (
                    <li key={i}>Row {e.row} ({e.serial_number}): {e.reason === 'duplicate_in_file' ? 'duplicate in file' : 'already exists in your fleet'}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setStep('upload'); setRows([]); setResult(null); }}>
                Import another file
              </Button>
              <Button className="btn-volt" onClick={() => navigate('/assets')} data-testid="done-view-assets-btn">
                View all assets →
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const Stepper = ({ step, source }) => {
  const isSql = source === 'allocate_sql';
  const steps = isSql
    ? [
        { id: 'upload', label: 'Connect' },
        { id: 'preview', label: 'Preview' },
        { id: 'done', label: 'Done' },
      ]
    : [
        { id: 'upload', label: 'Upload' },
        { id: 'map', label: 'Map columns' },
        { id: 'preview', label: 'Preview' },
        { id: 'done', label: 'Done' },
      ];
  const activeIdx = steps.findIndex((s) => s.id === step);
  return (
    <ol className="flex flex-wrap gap-2 text-xs">
      {steps.map((s, i) => {
        const done = i < activeIdx;
        const active = i === activeIdx;
        return (
          <li
            key={s.id}
            className={`px-3 py-1.5 rounded-full border ${
              active
                ? 'border-[#FFD108]/40 bg-[#FFD108]/10 text-[#FFD108] font-semibold'
                : done
                ? 'border-green-500/30 bg-green-500/[0.04] text-green-400'
                : 'border-border text-muted-foreground'
            }`}
          >
            {done ? '✓ ' : ''}{i + 1}. {s.label}
          </li>
        );
      })}
    </ol>
  );
};

export default AssetImportPage;
